import  { motion } from 'framer-motion';
import Hero from '../components/Hero';
import FeaturedProducts from '../components/FeaturedProducts';
import Collections from '../components/Collections';
import Testimonials from '../components/Testimonials';
import InstagramFeed from '../components/InstagramFeed';
import ParallaxSection from '../components/ParallaxSection';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

const HomePage = () => {
  const [ref, isInView] = useIntersectionObserver<HTMLDivElement>({ threshold: 0.1, once: true });

  return (
    <div>
      <Hero />
      
      <section ref={ref} className="py-16">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl mb-6 section-title">Our Craftsmanship</h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              At Lustre & Co., every piece is handcrafted with meticulous attention to detail. 
              Our master jewelers combine traditional techniques with modern innovation to create 
              timeless treasures that will be cherished for generations.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/about" 
                className="inline-block border border-gold text-gold hover:bg-gold hover:text-white px-8 py-3 uppercase tracking-wide text-sm transition-colors duration-300"
              >
                Our Story
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      <FeaturedProducts />
      
      <ParallaxSection 
        image="https://images.unsplash.com/photo-1605100804763-247f67b3557e?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80"
        title="Elegance in Every Detail"
        subtitle="Our artisans meticulously craft each piece to perfection"
      />
      
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1611591437281-460bfbe1220a?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80" 
                alt="Custom jewelry design process" 
                className="w-full h-auto"
              />
            </motion.div>
            <motion.div 
              className="p-6 md:p-12"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-serif text-3xl mb-4">Custom Creations</h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Our bespoke service allows you to create a truly unique piece that tells your personal story. 
                From initial sketch to the final masterpiece, our artisans will guide you through every step 
                of the design journey, ensuring your vision comes to life exactly as you imagined.
              </p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/custom-jewelry" 
                  className="inline-block bg-gold hover:bg-gold-dark text-white px-8 py-3 uppercase tracking-wide text-sm transition-colors duration-300"
                >
                  Explore Custom Jewelry
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <Collections />
      
      <ParallaxSection 
        image="https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80"
        title="Timeless Beauty"
        subtitle="Jewelry that transcends trends and becomes an heirloom"
        reversed={true}
      />
      
      <Testimonials />
      <InstagramFeed />
    </div>
  );
};

export default HomePage;
 